/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import dayjs from "dayjs";
import { When } from "react-if";
import styled from "styled-components";
import { ListLoader, LoadMore, theme } from "@nordcloud/gnui";
import type {
  EventActionAttempt,
  ResourceExecutionErrorCodeCount,
} from "~/generated/graphql";
import { LoaderWrap, NoData } from "~/components";
import { ResourceLoader } from "~/components/resourceGroup/ResourceLoader";
import {
  maxSearchLength,
  searchDelay,
  SearchInput,
} from "~/components/SearchInput";
import {
  getNoSearchResultsMessage,
  minSearchLength,
} from "~/components/SearchInput/const";
import { dateFormat } from "~/constants";
import { useElementHeight } from "~/hooks";
import { isNotNil } from "~/tools";
import { useEvent } from "~/views/events/EventsDetails/EventProvider";
import { EventResourceExecutionErrorMessages } from "~/views/events/EventsDetails/Tabs/Details/components/ActionBox/components/EventResourceExecutionErrorMessages";
import { EventResourceHeader } from "~/views/events/EventsDetails/Tabs/Details/components/ActionBox/components/EventResourceHeader";
import { useEventDetailsTab } from "~/views/events/EventsDetails/Tabs/Details/EventDetailsTabProvider";
import type { EventResourceGroup } from "~/views/events/EventsDetails/Tabs/Details/hooks/useEventResourceController/types";
import type { EventEntity } from "~/views/events/types";
import { EventResourceGroupItem } from "./EventResourceGroupItem";
import { ResourcesNoData } from "./ResourcesNoData";

type Props = {
  eventEntity: EventEntity;
  attempt?: EventActionAttempt;
  totalCount: number;
  errorCodeCounts: ResourceExecutionErrorCodeCount[];
};

const LOADER_ITEM_HEIGHT = 80;
const DEFAULT_LOADER_ITEMS = 4;

export function EventResourcesSection({
  eventEntity,
  attempt,
  totalCount,
  errorCodeCounts,
}: Props) {
  const { event } = useEvent();
  const { id: eventId } = event;
  const { resourceController } = useEventDetailsTab();
  const { resourceSearchState, updateResourceSearchState } = resourceController;

  const {
    groups,
    viewState,
    showHeader,
    showLoader,
    showLoadMore,
    loadRequest,
    loadMore,
    loadLess,
    canLoadLess,
    currentResourceCount,
  } = resourceController;

  const eventActionId = attempt?.id ?? eventEntity.id;
  const eventActionEndTime = eventEntity?.globalState?.endTime
    ? dayjs(eventEntity?.globalState?.endTime).format(
        dateFormat.dayMonthYearHourMinute
      )
    : undefined;

  const { ref: sectionRef, height: sectionHeight } = useElementHeight({
    deps: [viewState, showHeader, showLoadMore],
    condition: viewState === "content",
  });

  const renderResourceContent = useMemo(() => {
    switch (viewState) {
      case "initialLoading":
      case "searchLoading":
        return null;
      case "searchHint":
        return (
          <NoData
            customCss={{ flexDirection: "column" }}
            hasIcon
            message={getNoSearchResultsMessage()}
          />
        );
      case "searchEmpty":
        return (
          <NoData
            customCss={{ flexDirection: "column" }}
            hasIcon
            message="There are no Resources."
          />
        );
      case "content":
        return (
          <ContentWrap>
            {groups.map((group: EventResourceGroup, index) => (
              <EventResourceGroupItem
                key={group.resourceGroupId ?? `ungrouped-${index}`}
                states={(group.resourceStates ?? []).filter(isNotNil)}
                resourceGroup={group.resourceGroup}
                resourceGroupId={group.resourceGroupId}
                resourceGroupStatus={group.resourceGroupStatus}
                eventId={eventId}
                eventActionId={eventActionId}
                actionStatus={
                  attempt?.state?.status ?? eventEntity?.globalState?.status
                }
                {...{ eventActionEndTime }}
              />
            ))}
          </ContentWrap>
        );
      case "noResources":
        return <ResourcesNoData />;
      case "waiting":
      default:
        return (
          <NoData
            hasIcon
            customCss={{ flexDirection: "column" }}
            message={
              "Resource statuses will be available when this action begins. Please wait."
            }
          />
        );
    }
  }, [
    attempt?.state?.status,
    eventActionEndTime,
    eventActionId,
    eventEntity?.globalState?.status,
    eventId,
    groups,
    viewState,
  ]);

  const handleSearch = (phrase: string) => {
    updateResourceSearchState({ searchPhrase: phrase, page: undefined });
  };

  return (
    <div
      ref={sectionRef}
      css={{ height: "100%", lineHeight: "0", overflow: "hidden" }}
    >
      <When condition={showHeader}>
        <>
          <EventResourceHeader eventEntity={eventEntity} attempt={attempt}>
            <SearchWrap>
              <SearchInput
                placeholder="Type a Keyword (e.g., Resource Name, Resource ID, etc.)"
                value={resourceSearchState.searchPhrase ?? ""}
                onChange={handleSearch}
                onSubmit={handleSearch}
                disabled={viewState === "initialLoading"}
                debounceMs={searchDelay}
                minLength={minSearchLength}
                maxLength={maxSearchLength}
              />
            </SearchWrap>
          </EventResourceHeader>
          <EventResourceExecutionErrorMessages
            errorCodeCounts={errorCodeCounts}
          />
        </>
      </When>
      <LoaderWrap
        loading={showLoader}
        Component={() => (
          <ListLoader
            itemsNumber={
              sectionHeight &&
              viewState !== "searchLoading" &&
              viewState !== "searchClearLoading"
                ? Math.round(sectionHeight / LOADER_ITEM_HEIGHT)
                : DEFAULT_LOADER_ITEMS
            }
          >
            <ResourceLoader />
          </ListLoader>
        )}
      >
        <div css={{ lineHeight: theme.lineHeight }}>
          {renderResourceContent}
        </div>
      </LoaderWrap>

      <When condition={showLoadMore}>
        <LoadMore
          currentCount={currentResourceCount}
          total={totalCount ?? currentResourceCount}
          onLoadMore={() => loadMore()}
          onLoadLess={canLoadLess ? () => loadLess() : undefined}
          isLoading={loadRequest !== null}
          css={{ marginTop: theme.spacing.spacing04 }}
        />
      </When>
    </div>
  );
}

const ContentWrap = styled.div`
  & > :not(:first-child) {
    margin-top: ${theme.spacing.spacing04};
  }
  word-break: break-word;
`;

const SearchWrap = styled.div`
  width: 100%;
`;
